import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';

import { filesAPIService } from '~/api/files';
import QRCodeCornerStyle from '~/components/QRCodeDesign/QRCodeCornerStyle/QRCodeCornerStyle';
import QRCodeCorrectionLevel from '~/components/QRCodeDesign/QRCodeCorrectionLevel';
import QRCodeFrame from '~/components/QRCodeDesign/QRCodeFrame';
import QRCodeLogo from '~/components/QRCodeDesign/QRCodeLogo/QRCodeLogo';
import QRCodePattern from '~/components/QRCodeDesign/QRCodePattern/QRCodePattern';
import { BASIC_GRADIENT_STYLES, DESKTOP_MIN_WIDTH } from '~/constants/common';
import { useAppDispatch } from '~/hooks/useRedux';
import { updateQrCodeThunk } from '~/store/reducers/qr-code';
import { getQrCodeReducerData } from '~/store/reducers/qr-code/selectors';
import { getStepperData } from '~/store/reducers/stepepr/selectors';
import { QrCodeLogoState } from '~/types/builder';
import {
  ChangeGradientParams,
  ChangeIsGradientParams,
  QrCodeCorrectionLevel,
  QrCodeFrameItem,
  QrCodeStyle,
} from '~/types/qrCode';
import { getFileFromLocalSource, isRequiredFieldPresent } from '~/utils/common';
import { useErrorClassObserver } from '~/utils/hooks';

const DEFAULT_COLOR = '#000000';

const CreateQrFinishStep = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();

  const isDesktop = useMediaQuery(theme.breakpoints.up(DESKTOP_MIN_WIDTH));

  const qrCodeReducer = useSelector(getQrCodeReducerData) || [];
  const { isMicrositeDataValid } = useSelector(getStepperData);

  const [selectedLogo, setSelectedLogo] = useState({ name: 'empty', file: null });

  // TODO Remove in the future, used because there no forms to normally handle all fields validations
  const isErrorClass = useErrorClassObserver();

  useEffect(() => {
    isRequiredFieldPresent(dispatch, isMicrositeDataValid);
  }, [isErrorClass]);

  useEffect(() => {
    (async function () {
      if (qrCodeReducer.data.embeddedLogo) {
        const imgResponse = await fetch(qrCodeReducer.data.embeddedLogo);
        const blob = await imgResponse.blob();
        const logoFile = new File([blob], qrCodeReducer.data.embeddedLogo, blob);

        setSelectedLogo({ name: 'empty', file: logoFile, fileURL: qrCodeReducer.data.embeddedLogo });
      }
    })();
  }, []);

  const { mutate: uploadImage } = useMutation({
    mutationFn: filesAPIService.uploadImage,
    onSuccess: (response) => {
      dispatch(
        updateQrCodeThunk({
          embedded_logo_id: response.data,
        }),
      );
    },
  });

  const handleChangeLogo = async (newLogo: QrCodeLogoState) => {
    if (newLogo.name === 'empty') {
      void handleDeleteUploadedLogo();
    } else {
      setSelectedLogo(newLogo);
      const fileURL = await getFileFromLocalSource(window.URL.createObjectURL(newLogo.file));
      uploadImage(fileURL);
    }
  };

  const handleDeleteUploadedLogo = async () => {
    setSelectedLogo({ name: 'empty', file: null });

    dispatch(updateQrCodeThunk({ embedded_logo_id: null }));
  };

  const handleChangeFrame = (newFrame: QrCodeFrameItem) => {
    dispatch(
      updateQrCodeThunk({
        style: {
          ...qrCodeReducer.data?.style,
          frame: {
            ...qrCodeReducer.data?.style?.frame,
            style: newFrame.id,
            phrase_color: newFrame.defaultTextColor,
          },
        },
      }),
    );
  };

  const handleChangeShape = (newShape: string) => {
    dispatch(
      updateQrCodeThunk({
        style: {
          ...qrCodeReducer.data?.style,
          shape: {
            ...qrCodeReducer.data?.style?.shape,
            style: newShape,
          },
        },
      }),
    );
  };

  const handleChangeLevel = (newLevel: QrCodeCorrectionLevel) => {
    dispatch(
      updateQrCodeThunk({
        style: {
          ...qrCodeReducer.data?.style,
          error_correction_level: newLevel,
        },
      }),
    );
  };

  const handleChangeColor = (newColor: string) => {
    dispatch(
      updateQrCodeThunk({
        style: {
          ...qrCodeReducer.data?.style,
          shape: {
            ...qrCodeReducer.data?.style?.shape,
            color: newColor,
          },
        },
      }),
    );
  };

  const handleChangeGradient = ({ field, value, parentField, parentDbField }: ChangeGradientParams) => {
    dispatch(
      updateQrCodeThunk({
        style: {
          ...qrCodeReducer.data?.style,
          [parentDbField]: {
            ...qrCodeReducer.data?.style[parentField],
            color: {
              ...qrCodeReducer.data?.style[parentField].color,
              [field]: value,
            },
          },
        },
      }),
    );
  };

  const handleChangeIsGradient = ({ field, checked, defaultValue, dbField }: ChangeIsGradientParams) => {
    if (checked) {
      dispatch(
        updateQrCodeThunk({
          style: {
            ...qrCodeReducer.data?.style,
            [dbField]: {
              ...qrCodeReducer.data?.style[field],
              color: {
                ...BASIC_GRADIENT_STYLES,
                color: defaultValue ?? DEFAULT_COLOR,
              },
            },
          },
        }),
      );
    } else {
      dispatch(
        updateQrCodeThunk({
          style: {
            ...qrCodeReducer.data?.style,
            [dbField]: {
              ...qrCodeReducer.data?.style[field],
              color: DEFAULT_COLOR,
            },
          },
        }),
      );
    }
  };

  const handleChangeBorderStyle = (newBorderStyle: number) => {
    dispatch(
      updateQrCodeThunk({
        style: {
          ...qrCodeReducer.data?.style,
          corner_border: {
            ...qrCodeReducer.data?.style?.cornerBorder,
            style: newBorderStyle,
          },
        },
      }),
    );
  };

  const handleChangeCenterStyle = (newCenterStyle: number) => {
    dispatch(
      updateQrCodeThunk({
        style: {
          ...qrCodeReducer.data?.style,
          corner_center: {
            ...qrCodeReducer.data?.style?.cornerCenter,
            style: newCenterStyle,
          },
        },
      }),
    );
  };

  const handleChangeBorderColor = (newBorderColor: string) => {
    dispatch(
      updateQrCodeThunk({
        style: {
          ...qrCodeReducer.data?.style,
          corner_border: {
            ...qrCodeReducer.data?.style?.cornerBorder,
            color: newBorderColor,
          },
        },
      }),
    );
  };

  const handleChangeCenterColor = (newCenterColor: string) => {
    dispatch(
      updateQrCodeThunk({
        style: {
          ...qrCodeReducer.data?.style,
          corner_center: {
            ...qrCodeReducer.data?.style?.cornerCenter,
            color: newCenterColor,
          },
        },
      }),
    );
  };

  const handleSwitchColors = () => {
    dispatch(
      updateQrCodeThunk({
        style: {
          ...qrCodeReducer.data?.style,
          corner_center: {
            ...qrCodeReducer.data?.style?.cornerCenter,
            color: qrCodeReducer.data?.style?.cornerBorder?.color,
          },
          corner_border: {
            ...qrCodeReducer.data?.style?.cornerBorder,
            color: qrCodeReducer.data?.style?.cornerCenter?.color,
          },
        },
      }),
    );
  };

  const handleUpdateQrStyle = (styles: QrCodeStyle) => {
    dispatch(
      updateQrCodeThunk({
        style: styles,
      }),
    );
  };

  return (
    <Box sx={{ paddingBottom: '30px', flex: 1, maxWidth: isDesktop ? undefined : 'calc(100dvw - 32px)' }}>
      <QRCodeFrame
        frames={qrCodeReducer.qrCodeStyles?.frames}
        defaultSelectedFrame={qrCodeReducer.data?.style?.frame?.style}
        onSelectFrame={handleChangeFrame}
        qrStyles={qrCodeReducer.data?.style}
        onUpdateQrStyles={handleUpdateQrStyle}
      />
      <QRCodePattern
        defaultSelectedShape={qrCodeReducer.data?.style?.shape?.style}
        onSelectShape={handleChangeShape}
        isAdvanced
        onChangeColor={handleChangeColor}
        onChangeGradient={(params) => handleChangeGradient({ ...params, parentField: 'shape', parentDbField: 'shape' })}
        onChangeIsGradient={(e) =>
          handleChangeIsGradient({
            field: 'shape',
            dbField: 'shape',
            checked: e.target.checked,
            defaultValue: qrCodeReducer.data?.style?.shape?.color,
          })
        }
        qrCodeStyles={qrCodeReducer.data?.style}
      />
      <QRCodeCornerStyle
        onChangeBorderStyle={handleChangeBorderStyle}
        onChangeCenterStyle={handleChangeCenterStyle}
        onSwitchBorderAndCenterColors={handleSwitchColors}
        onChangeBorderColor={handleChangeBorderColor}
        onChangeCenterColor={handleChangeCenterColor}
        onChangeBorderGradient={(params) =>
          handleChangeGradient({ ...params, parentDbField: 'corner_border', parentField: 'cornerBorder' })
        }
        onChangeCenterGradient={(params) =>
          handleChangeGradient({ ...params, parentDbField: 'corner_center', parentField: 'cornerCenter' })
        }
        onChangeIsBorderGradient={(e) =>
          handleChangeIsGradient({
            dbField: 'corner_border',
            field: 'cornerBorder',
            checked: e.target.checked,
            defaultValue: qrCodeReducer?.data?.style?.cornerBorder?.color,
          })
        }
        onChangeIsCenterGradient={(e) =>
          handleChangeIsGradient({
            dbField: 'corner_center',
            field: 'cornerCenter',
            checked: e.target.checked,
            defaultValue: qrCodeReducer?.data?.style?.cornerCenter?.color,
          })
        }
        qrCodeStyles={qrCodeReducer?.data?.style}
      />
      <QRCodeCorrectionLevel
        levels={qrCodeReducer.qrCodeStyles?.errorCorrectionLevels}
        defaultSelectedLevel={qrCodeReducer.data?.style?.errorCorrectionLevel}
        onSelectLevel={handleChangeLevel}
      />
      <QRCodeLogo
        selectedLogo={selectedLogo}
        onSelectLogo={handleChangeLogo}
        onDeleteUploadedLogo={handleDeleteUploadedLogo}
      />
    </Box>
  );
};

export default CreateQrFinishStep;
